import { useState, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import type { AgentParameterDto } from '../../types/agent';
import { ParameterInput } from './ParameterInput';

interface ParameterFormProps {
  parameters: AgentParameterDto[];
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  errors?: Record<string, string>;
}

/**
 * Dynamic form component for agent parameters.
 */
export const ParameterForm = ({ parameters, values, onChange, errors }: ParameterFormProps) => {
  const [localValues, setLocalValues] = useState<Record<string, any>>(values);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const handleParameterChange = (key: string, value: any) => {
    const newValues = { ...localValues, [key]: value };
    setLocalValues(newValues);
    onChange(newValues);
  };

  if (parameters.length === 0) {
    return (
      <Alert severity="info">This agent does not require any parameters.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Agent Parameters
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure parameters for this agent session
      </Typography>
      {parameters.map((param) => (
        <ParameterInput
          key={param.key}
          parameter={param}
          value={localValues[param.key]}
          onChange={(value) => handleParameterChange(param.key, value)}
          error={errors?.[param.key]}
        />
      ))}
    </Box>
  );
};
